import { z } from 'zod';

// Lista negra de dominios de emails temporales/desechables
const BLOCKED_EMAIL_DOMAINS = [
  // Servicios populares de email temporal
  'tempmail.com', 'temp-mail.org', 'tempmail.net', 'temp-mail.io',
  'mailinator.com', 'mailinator.net', 'mailinator.org',
  'guerrillamail.com', 'guerrillamail.net', 'guerrillamail.org', 'guerrillamail.info',
  'sharklasers.com', 'guerrillamailblock.com',
  '10minutemail.com', '10minutemail.net', '10minmail.com',
  'yopmail.com', 'yopmail.fr', 'yopmail.net',
  'throwaway.email', 'throwawaymail.com',
  'fakeinbox.com', 'fakemailgenerator.com',
  'getnada.com', 'nada.email',
  'dispostable.com', 'disposablemail.com',
  'maildrop.cc', 'mailnesia.com',
  'trashmail.com', 'trashmail.net', 'trashmail.org',
  'mohmal.com', 'tempail.com',
  'emailondeck.com', 'mintemail.com',
  'mytemp.email', 'tempinbox.com',
  'burnermail.io', 'mailcatch.com',
  'spamgourmet.com', 'spamex.com',
  'getairmail.com', 'discard.email',
  'mailsac.com', 'inboxkitten.com',
  'tempmailo.com', 'emailfake.com',
  'crazymailing.com', 'tempsky.com',
  'tmails.net', 'emlpro.com',
  'fakemail.net', 'mailforspam.com',
  'instant-mail.de', 'wegwerfmail.de',
  'mail-temporaire.fr', 'jetable.org',
  // Otros comunes
  'mailnator.com', 'binkmail.com', 'safetymail.info',
  'spamfree24.org', 'firemailbox.club', 'armyspy.com',
  'cuvox.de', 'dayrep.com', 'einrot.com', 'fleckens.hu',
  'gustr.com', 'jourrapide.com', 'rhyta.com', 'superrito.com',
  'teleworm.us', 'tempr.email', 'trbvm.com',
];

// Función para verificar si el email es de un dominio bloqueado
const isDisposableEmail = (email) => {
  const domain = email.toLowerCase().split('@')[1];
  return BLOCKED_EMAIL_DOMAINS.includes(domain);
};

// Esquema para información personal
export const personalInfoSchema = z.object({
  first_name: z
    .string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(255, 'El nombre no puede exceder 255 caracteres'),
  last_name: z
    .string()
    .min(2, 'El apellido debe tener al menos 2 caracteres')
    .max(255, 'El apellido no puede exceder 255 caracteres'),
  national_id: z
    .string()
    .min(10, 'La cédula debe tener al menos 10 dígitos')
    .max(20, 'La cédula no puede exceder 20 caracteres')
    .regex(/^[0-9]+$/, 'La cédula solo debe contener números'),
});

// Esquema para información de contacto
export const contactInfoSchema = z.object({
  phone: z
    .string()
    .min(10, 'El teléfono debe tener al menos 10 dígitos')
    .max(20, 'El teléfono no puede exceder 20 caracteres')
    .regex(/^[+]?[0-9\s-]+$/, 'Formato de teléfono inválido'),
  email: z
    .string()
    .email('Ingresa un correo electrónico válido')
    .max(255, 'El correo no puede exceder 255 caracteres')
    .refine(
      (email) => !isDisposableEmail(email),
      'No se permiten correos temporales. Usa un correo personal válido.'
    ),
  city_id: z
    .number({ required_error: 'Selecciona una ciudad' })
    .min(1, 'Selecciona una ciudad'),
});

// Esquema para documentos
export const documentsSchema = z.object({
  national_id_file: z
    .any()
    .refine((file) => file instanceof File, 'Debes subir tu cédula de identidad'),
  license_file: z
    .any()
    .refine((file) => file instanceof File, 'Debes subir tu licencia de conducir'),
  registration_file: z
    .any()
    .optional(),
});

// Esquema completo para la solicitud
export const fullApplicationSchema = z.object({
  applicant_type: z.literal('driver'),
  first_name: z.string().min(2).max(255),
  last_name: z.string().min(2).max(255),
  national_id: z.string().min(10).max(20),
  phone: z.string().min(10).max(20),
  email: z.string().email().max(255),
  city_id: z.number().min(1),
});

// Validación de archivos (solo imágenes - el OCR del backend solo procesa imágenes)
export const validateFile = (file) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!file) return { valid: false, error: 'No se seleccionó ningún archivo' };
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Solo se permiten imágenes (JPG, PNG o WebP)' };
  }
  if (file.size > maxSize) {
    return { valid: false, error: 'La imagen no puede exceder 10MB' };
  }

  return { valid: true, error: null };
};
